/**
 * Browser file-picker prompt with a caller-supplied `accept` filter. Lives in
 * the library so app code never creates DOM elements directly. `accept` is any
 * valid HTML input accept string (e.g. "image/*" or
 * "image/*,application/pdf,video/mp4").
 */
export function pickFiles(
  accept: string,
  multiple: boolean,
  capture?: "user" | "environment",
): Promise<File[]> {
  return new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = accept;
    input.multiple = multiple;
    // On touch devices a capture hint opens the camera directly instead of the
    // library picker; desktop browsers ignore it.
    if (capture) input.setAttribute("capture", capture);
    input.onchange = () => resolve(Array.from(input.files ?? []));
    // Fired by modern browsers when the picker is dismissed without a choice.
    input.oncancel = () => resolve([]);
    input.click();
  });
}

/** Resolves with the chosen image File, or null when dismissed. */
export async function pickImageFile(): Promise<File | null> {
  const [file] = await pickFiles("image/*", false);
  return file ?? null;
}

/** Multi-select image variant — resolves with the chosen Files in picker
 * order, or an empty array when dismissed. */
export function pickImageFiles(): Promise<File[]> {
  return pickFiles("image/*", true);
}

/** Camera-first capture: on touch devices the rear camera opens directly;
 * desktop browsers fall back to the normal picker. Single shot per prompt. */
export function captureCameraImage(): Promise<File[]> {
  return pickFiles("image/*", false, "environment");
}
